import { authFetch } from "@/lib/api-client";
import type { AuditActionValue, AuditTargetTypeValue } from "@nexus/contracts";

// ─── Types ───────────────────────────────────────────────────────────

export interface AuditLogItem {
  id: string;
  tenantId: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  actorDisplayName: string | null;
  action: AuditActionValue;
  targetType: AuditTargetTypeValue;
  targetId: string;
  targetName: string | null;
  targetEmail: string | null;
  eventName: string;
  correlationId: string | null;
  ip: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface PaginatedAuditLogs {
  items: AuditLogItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AuditLogFilters {
  /** Single action or array of actions (joined with `,` for the BFF). */
  actionIn?: AuditActionValue | AuditActionValue[];
  actorId?: string;
  fromDate?: string;
  toDate?: string;
  cursor?: string;
  limit?: number;
}

// ─── API functions ───────────────────────────────────────────────────

function buildParams(filters: AuditLogFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.actionIn) {
    const value = Array.isArray(filters.actionIn) ? filters.actionIn.join(",") : filters.actionIn;
    if (value.length > 0) p.set("actionIn", value);
  }
  if (filters.actorId)  p.set("actorId",  filters.actorId);
  if (filters.fromDate) p.set("fromDate", filters.fromDate);
  if (filters.toDate)   p.set("toDate",   filters.toDate);
  if (filters.cursor)   p.set("cursor",   filters.cursor);
  if (filters.limit)    p.set("limit",    String(filters.limit));
  return p;
}

export async function fetchGlobalAuditLogs(filters: AuditLogFilters = {}): Promise<PaginatedAuditLogs> {
  const params = buildParams(filters);
  const qs = params.toString();
  const { data } = await authFetch<PaginatedAuditLogs>(`/audit-logs${qs ? `?${qs}` : ""}`);
  return data;
}

export async function fetchUserAuditLogs(
  userId: string,
  filters: AuditLogFilters = {},
): Promise<PaginatedAuditLogs> {
  const params = buildParams(filters);
  const qs = params.toString();
  const { data } = await authFetch<PaginatedAuditLogs>(
    `/audit-logs/users/${encodeURIComponent(userId)}${qs ? `?${qs}` : ""}`,
  );
  return data;
}

export async function fetchAccessAuditLogs(
  accessId: string,
  filters: AuditLogFilters = {},
): Promise<PaginatedAuditLogs> {
  const params = buildParams(filters);
  const qs = params.toString();
  const { data } = await authFetch<PaginatedAuditLogs>(
    `/audit-logs/access/${encodeURIComponent(accessId)}${qs ? `?${qs}` : ""}`,
  );
  return data;
}

export async function fetchTenantAuditLogs(
  tenantId: string,
  filters: AuditLogFilters = {},
): Promise<PaginatedAuditLogs> {
  const params = buildParams(filters);
  const qs = params.toString();
  const { data } = await authFetch<PaginatedAuditLogs>(
    `/audit-logs/tenants/${encodeURIComponent(tenantId)}${qs ? `?${qs}` : ""}`,
  );
  return data;
}
